import {
  ApplicationInsights,
  SeverityLevel,
  type IExceptionTelemetry,
  type ITraceTelemetry,
} from '@microsoft/applicationinsights-web';

/**
 * Client-side telemetry sink. Wraps `@microsoft/applicationinsights-web` so callers
 * (ErrorBoundary, MSAL logger, API client) interact with a small surface area and
 * tolerate the connection-string-absent local-dev case without crashing.
 *
 * PII discipline: do not pass user emails, names, or claim values into the sink.
 * `SanitizedTelemetry` exists so call sites are nudged toward scrubbed payloads.
 */

export interface SanitizedTelemetry {
  /** Stable identifier for the event source (e.g. `'errorBoundary'`, `'msal'`). */
  source: string;
  /** Free-form message — must not contain PII. */
  message: string;
  /** Optional structured properties — must not contain PII. */
  properties?: Record<string, string | number | boolean | undefined>;
}

let appInsights: ApplicationInsights | null = null;

/**
 * Initialize the telemetry sink. Idempotent — repeated calls are no-ops.
 *
 * No-ops (returns `null`) when `VITE_APPINSIGHTS_CONNECTION_STRING` is not set —
 * local dev does not require a connection string, and the surface gracefully
 * degrades so the app shell continues to render.
 */
export function initializeTelemetry(): ApplicationInsights | null {
  if (appInsights) {
    return appInsights;
  }

  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
  if (!connectionString || connectionString.length === 0) {
    return null;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        // Honor MSAL PII discipline at the telemetry tier too.
        disableTelemetry: false,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        disableExceptionTracking: false,
        disableAjaxTracking: false,
      },
    });
    appInsights.loadAppInsights();
    appInsights.trackPageView();
    return appInsights;
  } catch {
    // Failure to initialize must not crash the app shell.
    appInsights = null;
    return null;
  }
}

/**
 * Returns the initialized App Insights instance, or `null` if telemetry is not
 * configured (local dev).
 */
export function getTelemetry(): ApplicationInsights | null {
  return appInsights;
}

/**
 * Forward a caught exception to the telemetry sink. Safe to call when telemetry
 * is not configured — becomes a no-op.
 */
export function trackException(
  error: Error,
  source: string,
  properties?: SanitizedTelemetry['properties'],
): void {
  if (!appInsights) {
    return;
  }
  const envelope: IExceptionTelemetry = {
    exception: error,
    severityLevel: SeverityLevel.Error,
    properties: { source, ...properties },
  };
  appInsights.trackException(envelope);
}

/**
 * Forward a trace message to the telemetry sink. Safe to call when telemetry is
 * not configured — becomes a no-op.
 */
export function trackTrace(
  message: string,
  source: string,
  severity: SeverityLevel = SeverityLevel.Error,
  properties?: SanitizedTelemetry['properties'],
): void {
  if (!appInsights) {
    return;
  }
  const envelope: ITraceTelemetry = {
    message,
    severityLevel: severity,
    properties: { source, ...properties },
  };
  appInsights.trackTrace(envelope);
}

export { SeverityLevel };
